import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import clsx from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { Link, useParams } from 'react-router-dom';

import {
  createTimesheet,
  getTimesheet,
  getTimesheetBulk,
  getTimesheetCreate,
  getTimesheetIssueQuery,
  getTimesheetProjects,
  saveEditTimesheet,
  saveTimesheetBulk,
} from '../../../../packages/common/api';
import FormField from '../../../../packages/common/shared-ui/src/components/FormField/FormField';
import ClearIcon from '../../../../packages/common/shared-ui/src/icons/ClearIcon';
import { formatFields } from '../../../../packages/common/utils/fieldHelper';
import { formValidation } from '../../../../packages/common/utils/formValidation';
import { useTranslation } from '../../../../packages/common/utils/getLocalString';

const StyledModalRoot = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-container': {
    padding: '84px 0 20px 0',
  },
  '& .MuiPaper-root.MuiDialog-paper': {
    overflow: 'visible',
    padding: '20px',
    maxWidth: '600px',
    backgroundColor: '#F1F2F8',
    borderRadius: '16px',
    margin: 0,
    maxHeight: '100%',
  },
  modalPaper: {
    padding: theme.spacing(2.5, 5),
    overflowY: 'auto',
  },
}));
const StyledBlockWrapper = styled(Grid)(({ theme }) => ({
  flexWrap: 'nowrap!important',
  overflowY: 'auto',
  padding: '0 16px 16px 16px!important',
  backgroundColor: '#fff',
  borderRadius: '16px',
  '&::-webkit-scrollbar': {
    height: '6px',
    width: '6px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#7174AC',
    borderRadius: '5px',
  },
}));

const TimesheetsCreateEditModal = ({
  open,
  handleClose,
  handleSave,
  state,
  data,
  project,
}) => {
  const getLocalString = useTranslation();
  const [pageTitle, setPageTitle] = useState('');
  const ModalState = { CREATE: 'create', EDIT: 'edit', BULK_EDIT: 'bulk_edit' };
  const [modalState, setModalState] = useState(null);
  const location = useLocation();
  const pageParams = useParams();
  const navigate = useNavigate();
  const urlParams = useRef(new URLSearchParams(location.search));

  const PageState = { CREATE: 'create', EDIT: 'edit', BULK_EDIT: 'bulk_edit' };
  //const [pageState, setPageState] = useState(PageState.CREATE);
  const [loading, setLoading] = useState(true);
  const [formAllFields, setFormAllFields] = useState([]);
  const [errors, setErrors] = useState([]);
  const [projectField, setProjectField] = useState({
    fieldId: 'project',
    fieldName: getLocalString('SpentTimePopup', 'labels', 'project'),
    valueId: null,
    valueName: null,
    fieldDefinition: {
      fieldId: 'project',
      fieldName: getLocalString('SpentTimePopup', 'labels', 'project'),
      fieldFormat: 'list',
      isMultiple: false,
      isRequired: true,
      isReadonly: false,
      values: [],
    },
  });
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [searchFieldOptions, setSearchFieldOptions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [getParams, setGetParams] = useState({
    paramsChecked: false,
    params: {},
    id: null,
  });
  const [bulkTimesheets, setBulkTimesheets] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [modalData, setModalData] = useState(null);
  const [modalId, setModalId] = useState(null);
  const [userId, setUserId] = useState(null);

  const formState = useRef(
    new Map([
      [
        ModalState.CREATE,
        {
          getResponse: getTimesheetCreate,
          saveResponse: createTimesheet,
          pageTitle: 'page_title_spentTime',
        },
      ],
      [
        ModalState.EDIT,
        {
          getResponse: getTimesheet,
          saveResponse: saveEditTimesheet,
          pageTitle: 'page_title_spentTime',
        },
      ],
      [
        ModalState.BULK_EDIT,
        {
          getResponse: getTimesheetBulk,
          saveResponse: saveTimesheetBulk,
          pageTitle: 'page_title_spentTime_bulk',
        },
      ],
    ]),
  );

  useEffect(() => {
    if (state && open) {
      setModalState(state);
    }
  }, [state, open]);

  useEffect(() => {
    let title;
    if (modalState) {
      modalState === 'edit' &&
        (title = getLocalString('SpentTimePopup', 'editTitle'));
      modalState === 'create' &&
        (title = getLocalString('SpentTimePopup', 'createTitle'));
      modalState === 'bulk_edit' &&
        (title = getLocalString('SpentTimePopup', 'bulkTitle'));
    }
    setPageTitle(title);
  }, [getLocalString, modalState]);

  useEffect(() => {
    if (data) {
      setModalData(data);
      modalState === PageState.EDIT && setModalId(data);
    }
  }, [PageState.EDIT, data, modalState]);
  const clearModal = useCallback(() => {
    setModalData(null);
    setFormAllFields([]);
    setSelectedProject(null);
    setProjectField({
      fieldId: 'project',
      fieldName: getLocalString('SpentTimePopup', 'labels', 'project'),
      valueId: null,
      valueName: null,
      fieldDefinition: {
        fieldId: 'project',
        fieldName: getLocalString('SpentTimePopup', 'labels', 'project'),
        fieldFormat: 'list',
        isMultiple: false,
        isRequired: true,
        isReadonly: false,
        values: [],
      },
    });
    setBulkTimesheets([]);
    setModalId(null);
    setGetParams({ paramsChecked: false, params: {}, id: null });
    setSearchFieldOptions([]);
    setErrors([]);
    setLoading(false);
    setFieldsLoading(false);
    setSearchLoading(false);
    setModalState(null);
  }, []);
  const handleCloseButton = useCallback(() => {
    clearModal();
    handleClose();
  }, [clearModal, handleClose]);

  const handleError = useCallback(
    error => {
      if (error) {
        navigate(`/errors/error-${error.status}`);
      }
    },
    [navigate],
  );
  useEffect(() => {
    if (
      formAllFields &&
      formAllFields.find(item => item.fieldId === 'userId') !== undefined
    ) {
      const field = formAllFields.find(item => item.fieldId === 'userId');
      field.valueId && setUserId(field.valueId.valueId);
    }
  }, [formAllFields]);
  useEffect(() => {
    let ignore = false;
    async function getProjects() {
      const response = await getTimesheetProjects();
      if (!ignore) {
        return response;
      }
    }
    if (open && modalState === ModalState.CREATE && !project) {
      getProjects()
        .then(res => {
          const { data: projects } = res;

          setProjectField(prev => ({
            ...prev,
            fieldDefinition: {
              ...prev.fieldDefinition,
              values: projects.map(item => {
                return {
                  projectIdentifier: item.projectIdentifier,
                  valueId: item.projectId,
                  valueName: item.projectName,
                };
              }),
            },
          }));
        })
        .catch(error => handleError(error.response));
    }
    return () => {
      ignore = true;
    };
  }, [handleError, project, open, modalState]);

  useEffect(() => {
    let params = {};
    if (modalState === ModalState.CREATE) {
      selectedProject && (params.project = selectedProject.valueId);
    }
    project && (params.project = project);
    modalState === PageState.BULK_EDIT &&
      Array.isArray(modalData) &&
      (params.timesheets = modalData.join('||'));
    urlParams.current.get('issueId') &&
      (params.issue = urlParams.current.get('issueId'));
    pageParams.id && (params.issue = pageParams.id);
    if (modalState === PageState.CREATE || modalState === PageState.BULK_EDIT) {
      Object.values(params).length > 0 &&
        setGetParams({ paramsChecked: true, params: params, id: null });
    }
    if (modalState === PageState.EDIT) {
      modalId &&
        setGetParams({ paramsChecked: true, params: params, id: modalId });
    }
  }, [
    selectedProject,
    PageState.BULK_EDIT,
    PageState.CREATE,
    project,
    modalState,
    modalData,
    pageParams.id,
    PageState.EDIT,
    modalId,
  ]);

  useEffect(() => {
    let ignore = false;
    async function getPageData() {
      setFieldsLoading(true);
      const response = await formState.current
        .get(modalState)
        .getResponse(getParams.id, getParams.params);
      if (!ignore) {
        return response;
      }
    }
    if (getParams.paramsChecked && open) {
      getPageData()
        .then(res => {
          let fields = {};
          const { data: timesheetData } = res;
          const dataFields =
            modalState === 'bulk_edit'
              ? timesheetData.timesheet
              : timesheetData;
          dataFields.standards && (fields.standards = dataFields.standards);
          dataFields.customs && (fields.customs = dataFields.customs);
          timesheetData.project &&
            setProjectField(prev => ({
              ...prev,
              valueName: {
                valueId: timesheetData.project.projectId,
                valueName: timesheetData.project.projectName,
              },
              valueId: {
                valueId: timesheetData.project.projectId,
                valueName: timesheetData.project.projectName,
              },
              fieldDefinition: {
                ...prev.fieldDefinition,
                isReadonly: true,
              },
            }));
          timesheetData.timesheets &&
            setBulkTimesheets(timesheetData.timesheets);
          setFormAllFields(formatFields(fields));
          setFieldsLoading(false);
        })
        .catch(error => handleError(error.response));
    }
    return () => {
      ignore = true;
    };
  }, [
    PageState.EDIT,
    getParams,
    handleError,
    modalData,
    modalId,
    modalState,
    open,
  ]);

  const handleFormField = useCallback((data, field) => {
    if (field.fieldId === 'project') {
      setSelectedProject(data.valueId);
      setProjectField(prev => ({
        ...prev,
        valueName: data.valueId,
        valueId: data.valueName,
      }));
    } else {
      setFormAllFields(prevFields => {
        const pos = prevFields.map(e => e.fieldId).indexOf(field.fieldId);
        if (pos !== -1) {
          const fieldValue = {
            ...prevFields[pos],
            ...data,
          };
          return [
            ...prevFields.slice(0, pos),
            fieldValue,
            ...prevFields.slice(pos + 1),
          ];
        }
        return prevFields;
      });
    }
  }, []);

  const handleValidation = useCallback(() => {
    if (modalState === PageState.BULK_EDIT) {
      return false;
    }
    const validationResult = formValidation(formAllFields);
    setFormAllFields(validationResult.fields);
    setErrors(validationResult.errors);
    return validationResult.error;
  }, [PageState.BULK_EDIT, formAllFields, modalState]);

  const saveForm = useCallback(
    async action => {
      const validation = handleValidation();
      if (modalState !== PageState.BULK_EDIT && !projectField.valueId.valueId) {
        return;
      }
      if (validation) {
        return;
      }
      let changedField;
      let filledStandardFields;
      let filledCustomsFields;
      let requiredFieldsStandard;
      let requiredFieldsCustom;
      let changedFieldsStandard;
      let changedFieldsCustom;
      let resultStandardArray;
      let resultCustomsArray;
      changedField = formAllFields.filter(field => field.changed);
      //filledStandardFields = formAllFields.filter(
      //  field => field.valueId !== null && field.valueName !== '' && !field.custom,
      //);
      //filledCustomsFields = formAllFields.filter(
      //  field => field.valueId !== null && field.valueName !== '' && field.custom,
      //);
      modalState === PageState.CREATE &&
        (changedField = formAllFields.filter(
          field => field.changed && !field.fieldDefinition.isRequired,
        ));
      changedFieldsStandard = changedField.filter(field => !field.custom);
      changedFieldsCustom = changedField.filter(field => field.custom);
      modalState === PageState.CREATE &&
        (requiredFieldsStandard = formAllFields.filter(
          field => field.fieldDefinition.isRequired && !field.custom,
        ));
      modalState === PageState.CREATE &&
        (requiredFieldsCustom = formAllFields.filter(
          field => field.fieldDefinition.isRequired && field.custom,
        ));
      resultStandardArray = [...changedFieldsStandard];
      resultCustomsArray = [...changedFieldsCustom];
      modalState === PageState.CREATE &&
        (resultStandardArray = [
          ...requiredFieldsStandard,
          ...changedFieldsStandard,
        ]);
      modalState === PageState.CREATE &&
        (resultCustomsArray = [
          ...requiredFieldsCustom,
          ...changedFieldsCustom,
        ]);
      const numbers = ['float', 'progressbar', 'int'];
      const result = resultStandardArray.reduce((acc, field) => {
        let value = field.valueId;
        field.fieldDefinition.fieldFormat === 'date' &&
          field.valueId === '' &&
          (value = null);
        typeof field.valueId === 'object' &&
          (value =
            field.valueId.valueId !== ''
              ? Number(field.valueId.valueId)
              : null);
        numbers.includes(field.fieldDefinition.fieldFormat) &&
          (value = Number(field.valueId.replace(',', '.')));
        field.fieldDefinition.fieldFormat === 'bool' &&
          (value = field.valueId === 'true');
        field.deleted &&
          field.fieldDefinition.values &&
          (value = field.valueId);
        acc[field.fieldId] = value;

        return acc;
      }, {});
      const resultCustoms = resultCustomsArray.map(field => {
        let value = field.valueId.toString();
        typeof field.valueId === 'object' &&
          (value =
            field.valueId.valueId !== '' ? Number(field.valueId.valueId) : '');
        field.fieldDefinition.fieldFormat === 'bool' &&
          (value = (field.valueId === 'true').toString());
        numbers.includes(field.fieldDefinition.fieldFormat) &&
          (value = Number(field.valueId.replace(',', '.')));
        return {
          fieldId: field.fieldId,
          valueId: value.toString(),
        };
      });
      let data = {
        ...result,
        customs: resultCustoms,
      };
      if (modalState === PageState.BULK_EDIT) {
        data = {
          ids: modalData.map(item => Number(item)),
          timesheet: {
            ...result,
            customs: resultCustoms,
          },
        };
      }
      setLoading(true);
      try {
        let id = null;
        typeof modalData === 'string' && (id = modalData);
        const response = await formState.current
          .get(modalState)
          .saveResponse(id, data);
        if (response) {
          setLoading(false);
          clearModal();
          handleSave();
        }
      } catch (error) {
        setLoading(false);
        handleError(error.response);
      }
    },
    [
      clearModal,
      handleSave,
      handleValidation,
      modalState,
      PageState.BULK_EDIT,
      PageState.CREATE,
      projectField,
      formAllFields,
      modalData,
      handleError,
    ],
  );

  const handleSearchFieldInput = useCallback(
    async value => {
      setSearchLoading(true);
      const { valueId: projectValue } = projectField;
      let params = {
        search: value,
        project: projectValue.valueId,
      };
      userId && (params.user = userId);
      try {
        const response = await getTimesheetIssueQuery(params);
        if (response) {
          setSearchFieldOptions(response.data);
          setSearchLoading(false);
        }
      } catch (error) {
        console.error('ERROR WITH EDIT', error);
      }
    },
    [projectField, userId],
  );
  return (
    <>
      {modalState && (
        <StyledModalRoot
          key={`${modalState}-${open}-${modalId || ''}`}
          open={open}
          disableRestoreFocus
          fullWidth={true}
        >
          <Grid
            container
            wrap='nowrap'
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
            }}
          >
            <Typography variant='h1'>{pageTitle}</Typography>
            <IconButton disableRipple value='close' onClick={handleCloseButton}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                }}
              >
                <ClearIcon width={24} height={24} viewBox='0 0 24 24' />
              </Box>
            </IconButton>
          </Grid>
          <Grid
            container
            direction='column'
            sx={{
              overflow: 'hidden',
              flexWrap: 'nowrap',
            }}
          >
            {errors.length > 0 && (
              <Box>
                {errors.map((error, index) => (
                  <Typography
                    key={index}
                    variant='h3'
                    sx={{
                      color: '#E03737',
                    }}
                  >
                    {getLocalString('errors', error.message)}
                  </Typography>
                ))}
              </Box>
            )}
            {fieldsLoading && (
              <Grid
                container
                sx={{
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%',
                  zIndex: 99,
                  position: 'fixed',
                  top: 0,
                  left: 0,
                }}
              >
                <CircularProgress color='secondary' />
              </Grid>
            )}
            <Grid container direction={'column'} sx={{ mt: '4px' }}>
              {bulkTimesheets.length > 0 && (
                <Box
                  sx={{
                    marginTop: '4px',
                    marginBottom: '20px',
                  }}
                >
                  {bulkTimesheets.map((item, index) => {
                    return (
                      <Grid key={index}>
                        <Typography variant='h5'>
                          <Link
                            to={`/timesheets/${item.timeEntryId}/edit`}
                            target={'_blank'}
                            style={{ color: '#212346' }}
                          >
                            {item?.timeEntryName}
                          </Link>
                        </Typography>
                      </Grid>
                    );
                  })}
                </Box>
              )}
            </Grid>
            <StyledBlockWrapper
              container
              direction={'column'}
              sx={{
                padding: '0 32px 16px 32px',
              }}
            >
              {projectField && modalState !== ModalState.BULK_EDIT && (
                <FormField
                  field={projectField}
                  fieldEvent={handleFormField}
                ></FormField>
              )}
              {formAllFields.map((field, index) => (
                <FormField
                  key={index}
                  field={field}
                  fieldEvent={handleFormField}
                  changeSearchInput={handleSearchFieldInput}
                  searchLoading={searchLoading}
                  searchFieldOptions={searchFieldOptions}
                  bulk={modalState === PageState.BULK_EDIT}
                ></FormField>
              ))}
            </StyledBlockWrapper>
            <Grid
              container
              sx={{
                justifyContent: 'center',
                marginTop: 5,
              }}
            >
              <Box>
                <Button
                  disableRipple
                  className={clsx('defaultButton', 'primary')}
                  onClick={saveForm}
                >
                  <Typography
                    variant='h4'
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    {getLocalString('buttons', 'save')}
                  </Typography>
                </Button>
              </Box>
            </Grid>
          </Grid>
        </StyledModalRoot>
      )}
    </>
  );
};

export default TimesheetsCreateEditModal;
